import { uniqueId } from 'lodash';

import { copyListValues } from 'companySettings/components/PartFinderProductTypes/UpsertPartFinderProductType/utils';
import { AttributeStatus, FamilyAttributeSourceLevel } from 'companySettings/enums';
import {
  IPartFinderFamily,
  IPartFinderFamilyAttribute,
  IPartFinderFamilyPayload,
  IPartFinderProductType,
  IPartFinderProductTypeAttribute,
} from 'companySettings/types';

import { dropSingleGroups, normalizeGroups } from './groups';
import { IFamilyPartForm, IUpsertPartFinderFamilyFormData } from './types';
import { getAttributeKey, prunePartValues } from './utils';

export const mapPartFinderFamilyFormDefaults = (
  family?: IPartFinderFamily,
  parts: IFamilyPartForm[] = []
): IUpsertPartFinderFamilyFormData => ({
  parts,
  attributes: family?.attributes ?? [],
  attributesProductTypeId: family?.attributes?.length ? family.productTypeId : null,
  category: family ? { id: family.categoryId, name: family.categoryName ?? '' } : null,
  name: family?.name ?? '',
  productTypeId: family?.productTypeId ?? null,
});

// A family starts with every attribute of its product type's release, every value switched on.
export const mapFamilyAttributesFromProductType = (
  productType: IPartFinderProductType
): IPartFinderFamilyAttribute[] =>
  (productType?.attributes ?? []).map((attribute) => ({
    inUse: true,
    name: attribute.name,
    type: attribute.type,
    sourceAttributeId: attribute.id,
    sourceAttributeName: attribute.name,
    sourceAttributeVersionId: attribute.attributeVersionId,
    sourceLevel: FamilyAttributeSourceLevel.PRODUCT_TYPE,
    sourceValues: attribute.values,
    values: attribute.values,
  }));

export const isInheritedAttribute = (attribute: IPartFinderFamilyAttribute) =>
  FamilyAttributeSourceLevel.PRODUCT_TYPE === attribute.sourceLevel;

// What a product type change does with the inherited attributes of the old one.
export enum DetachedAttributes {
  CONVERT = 'convert',
  DELETE = 'delete',
}

// A product type change, in the wizard and the editor alike: the new type's attributes come in,
// every value on, and the inherited ones of the old type go (Delete) or stay as the family's own
// (Convert). A converted one keeps its values but writes back to no product type, and one whose
// name the new type has too takes the old type's name after its own, as a family's names are its
// own. One not saved yet gets a client key of its own - without a source its old key would be
// every converted attribute's - and `movedKeys` says where its part values go
// (movePartValueKeys()). Groups are put back together around the attributes that went.
export const mapAttributesForProductType = (
  attributes: IPartFinderFamilyAttribute[],
  productType: IPartFinderProductType | undefined,
  mode: DetachedAttributes,
  previousProductTypeName = ''
): { attributes: IPartFinderFamilyAttribute[]; movedKeys: Record<string, string> } => {
  const incoming = productType ? mapFamilyAttributesFromProductType(productType) : [];
  const incomingNames = new Set(incoming.map(({ name }) => name.trim().toLowerCase()));
  const movedKeys: Record<string, string> = {};

  const kept = (attributes ?? []).flatMap((attribute) => {
    if (!isInheritedAttribute(attribute)) {
      return [attribute];
    }

    if (DetachedAttributes.DELETE === mode) {
      return [];
    }

    const isNameTaken = incomingNames.has(attribute.name.trim().toLowerCase());
    const clientKey = attribute.id
      ? attribute.clientKey
      : attribute.clientKey ?? uniqueId('familyAttribute');

    if (!attribute.id && clientKey !== getAttributeKey(attribute)) {
      movedKeys[getAttributeKey(attribute)] = clientKey;
    }

    return [
      {
        ...attribute,
        clientKey,
        name:
          isNameTaken && previousProductTypeName
            ? `${attribute.name} (${previousProductTypeName})`
            : attribute.name,
        sourceAttributeId: null,
        sourceAttributeName: null,
        sourceAttributeVersionId: null,
        sourceLevel: null,
        sourceValues: [],
        updateList: false,
      },
    ];
  });

  return { attributes: normalizeGroups([...incoming, ...kept]), movedKeys };
};

// An attribute as the product type's attribute drawer edits it, and back. An inherited one goes
// without its source, so the drawer edits its values as typed ones and compares them with the
// product type's itself.
export const mapFamilyAttributeToDrawer = (
  attribute?: IPartFinderFamilyAttribute
): IPartFinderProductTypeAttribute | undefined =>
  attribute && {
    ...(attribute.id && { id: attribute.id }),
    name: attribute.name,
    type: attribute.type,
    sourceAttributeId: isInheritedAttribute(attribute) ? null : attribute.sourceAttributeId,
    sourceAttributeName: isInheritedAttribute(attribute) ? null : attribute.sourceAttributeName,
    sourceVersionNumber: isInheritedAttribute(attribute) ? null : attribute.sourceVersionNumber,
    updateList: attribute.updateList,
    values: attribute.values,
  };

// An inherited attribute keeps everything but its values and the write-back to the product type.
export const mapFamilyAttributeFromDrawer = (
  attribute: IPartFinderProductTypeAttribute,
  current?: IPartFinderFamilyAttribute
): IPartFinderFamilyAttribute =>
  current && isInheritedAttribute(current)
    ? { ...current, values: attribute.values, updateList: attribute.updateList }
    : {
        ...(current?.id && { id: current.id }),
        clientKey: current?.clientKey ?? uniqueId('familyAttribute'),
        group: current?.group ?? null,
        inUse: current?.inUse ?? true,
        name: attribute.name,
        type: attribute.type,
        sourceAttributeId: attribute.sourceAttributeId ?? null,
        sourceAttributeName: attribute.sourceAttributeName ?? null,
        sourceAttributeVersionId: null,
        sourceLevel: attribute.sourceAttributeId ? FamilyAttributeSourceLevel.COMPANY : null,
        sourceValues: [],
        sourceVersionNumber: attribute.sourceVersionNumber ?? null,
        updateList: attribute.updateList,
        values: attribute.values,
      };

// An edit also sends the revision it was made on, so the server can refuse it when someone else
// saved the family in the meantime. An attribute goes with the source version it was taken from
// and the values it uses, which the server copies from that version.
export const mapPartFinderFamilyPayload = (
  data: IUpsertPartFinderFamilyFormData,
  family?: IPartFinderFamily,
  status: AttributeStatus = AttributeStatus.DRAFT
): IPartFinderFamilyPayload => ({
  ...(family && { id: family.id, revision: family.revision }),
  status,
  name: data.name?.trim(),
  productTypeId: data.productTypeId,
  categoryId: data.category?.id,
  // A group left with one attribute, e.g. after a product type change, goes; nothing moves, so the
  // parts' attribute indexes below still hold.
  attributes: dropSingleGroups(data.attributes ?? []).map((attribute) => ({
    ...(attribute.id && { id: attribute.id }),
    group: attribute.group ?? null,
    inUse: attribute.inUse,
    name: attribute.name?.trim(),
    type: attribute.type,
    ...(attribute.sourceAttributeId && { sourceAttributeId: attribute.sourceAttributeId }),
    ...(attribute.sourceAttributeVersionId && {
      sourceAttributeVersionId: attribute.sourceAttributeVersionId,
    }),
    ...(attribute.sourceVersionNumber && { sourceVersionNumber: attribute.sourceVersionNumber }),
    ...(attribute.updateList && { updateList: true }),
    values: copyListValues(attribute.values),
  })),
  // Each part's values name their attribute by its index among the attributes sent.
  partsJson: JSON.stringify(
    prunePartValues(data.parts ?? [], data.attributes ?? []).map((part) => ({
      manufacturer_id: part.manufacturerId,
      part_number: part.partNumber,
      values: (data.attributes ?? [])
        .map((attribute, index) => ({ index, values: part.values?.[getAttributeKey(attribute)] }))
        .filter(({ values }) => values !== undefined)
        .map(({ index, values }) => ({
          attribute_index: index,
          values: values.map((value) =>
            value.numberValue !== undefined && value.numberValue !== null
              ? { number_value: value.numberValue }
              : { value: value.value }
          ),
        })),
    }))
  ),
});
